import { useState, useEffect } from "react";
import {
  Wallet,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  ArrowUpRight,
  ArrowDownLeft,
  QrCode,
  X,
  ExternalLink,
} from "lucide-react";
import QRCodeLib from "qrcode";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getBalance, getRecentTransactions } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

interface Transaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
}

export function WalletPage() {
  const [showBalance, setShowBalance] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [qrCodeDataURL, setQRCodeDataURL] = useState<string>("");
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [addressToUsername, setAddressToUsername] = useState<{
    [address: string]: string | undefined;
  }>({});
  const [loadingBalance, setLoadingBalance] = useState<boolean>(false);
  const [loadingTransactions, setLoadingTransactions] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const user = useSelector((state: RootState) => state.user) as any;
  const walletAddress = user?.smartWallet || "";
  const adminWallet = import.meta.env.VITE_Admin_Wallet?.toLowerCase() || "";

  useEffect(() => {
    const fetchBalance = async () => {
      if (!walletAddress) {
        setError("No wallet address found for the user.");
        toast.error("No wallet address found for the user.");
        return;
      }

      setLoadingBalance(true);
      try {
        const ethBalance = await getBalance(walletAddress);
        console.log("Fetched ETH balance:", ethBalance);
        setWalletBalance(ethBalance);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching ETH balance:", err.message);
        toast.error("Failed to fetch wallet balance. Please try again.");
        setError("Failed to fetch wallet balance: " + err.message);
      } finally {
        setLoadingBalance(false);
      }
    };

    fetchBalance();
  }, [walletAddress]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!walletAddress) {
        setError("No wallet address found for the user.");
        toast.error("No wallet address found for the user.");
        return;
      }

      setLoadingTransactions(true);
      try {
        const txs = await getRecentTransactions(walletAddress);

        // Filter out any transactions where the admin wallet is the recipient (fee payments)
        const filteredTxs = txs.filter((tx: Transaction) => {
          if (tx.type === "sent") {
            return tx.to.toLowerCase() !== adminWallet;
          }
          return true; // Keep all received transactions
        });

        console.log("Fetched and filtered transactions:", filteredTxs);
        setTransactions(filteredTxs);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching transactions:", err.message);
        toast.error("Failed to fetch transactions. Please try again.");
        setError("Failed to fetch transactions: " + err.message);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [walletAddress, adminWallet]);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const map: { [address: string]: string | undefined } = {};

        usersSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.username;
          }
        });

        vendorsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.name;
          }
        });

        setAddressToUsername(map);
      } catch (err: any) {
        console.error("Error fetching profiles:", err.message);
        toast.error("Failed to fetch profiles. Please try again.");
      }
    };

    fetchProfiles();
  }, []);

  useEffect(() => {
    if (walletAddress && showQRCode) {
      const generateQRCode = async () => {
        try {
          // Create a canvas to draw the QR code
          const canvas = document.createElement("canvas");
          await QRCodeLib.toCanvas(canvas, walletAddress, {
            width: 256,
            margin: 2,
            color: {
              dark: "#61CE70", // QR code dark color remains green
              light: "#FFFFFF", // Background white for contrast
            },
          });

          // Get the 2D context to add text
          const ctx = canvas.getContext("2d");
          if (ctx) {
            // Set text properties for watermark effect
            ctx.font = "bold 36px Arial"; // Increase font size for prominence
            ctx.fillStyle = "#000000"; // Text color black
            ctx.globalAlpha = 0.5; // Semi-transparent for watermark effect
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            // Reset globalAlpha for future canvas operations
            ctx.globalAlpha = 1.0;

            // Convert canvas to data URL
            const dataURL = canvas.toDataURL("image/png");
            setQRCodeDataURL(dataURL);
          }
        } catch (error) {
          console.error("Error generating QR code:", error);
          toast.error("Failed to generate QR code.");
        }
      };
      generateQRCode();
    }
  }, [walletAddress, showQRCode]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleRefresh = async () => {
    if (!walletAddress) {
      setError("No wallet address found for the user.");
      toast.error("No wallet address found for the user.");
      return;
    }

    setLoadingBalance(true);
    try {
      const ethBalance = await getBalance(walletAddress);
      console.log("Refreshed ETH balance:", ethBalance);
      setWalletBalance(ethBalance);
      setError(null);
      toast.success("Balance refreshed!");
    } catch (err: any) {
      console.error("Error refreshing ETH balance:", err.message);
      toast.error("Failed to refresh wallet balance.");
      setError("Failed to refresh wallet balance: " + err.message);
    } finally {
      setLoadingBalance(false);
    }
  };

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getCounterpartyDisplay = (tx: Transaction): string => {
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToUsername[counterparty] || truncateAddress(counterparty);
  };

  const viewOnBlockExplorer = (hash: string) => {
    window.open(`https://basescan.org/tx/${hash}`, "_blank");
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          My Wallet
        </h1>
        <p className="text-gray-400 text-lg">
          Manage your wallet balance and transactions
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {/* Wallet Card */}
      <div className="bg-gradient-to-br from-[#61CE70] via-[#55C165] to-[#50B060] rounded-3xl p-6 sm:p-8 mb-8 text-white shadow-2xl shadow-[#61CE70]/20 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 sm:w-40 sm:h-40 bg-white/10 rounded-full -translate-y-16 sm:-translate-y-20 translate-x-16 sm:translate-x-20"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/5 rounded-full translate-y-12 sm:translate-y-16 -translate-x-12 sm:-translate-x-16"></div>

        {/* Balance + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4 sm:gap-0 relative z-10">
          <div className="flex items-center space-x-3">
            <div className="p-3 sm:p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
              <Wallet className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <p className="text-3xl sm:text-4xl md:text-5xl font-bold">
              {loadingBalance
                ? "Loading..."
                : showBalance
                ? `${parseFloat(walletBalance)} USDC`
                : "••••••"}
            </p>
          </div>

          {/* Eye + QR buttons */}
          <div className="flex items-center space-x-2 self-start sm:self-auto">
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors"
            >
              {showBalance ? (
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
            <button
              onClick={() => setShowQRCode(true)}
              className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors"
            >
              <QrCode className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Address */}
        <div className="mb-6 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm opacity-90">Address:</span>
              <span className="font-mono text-xs sm:text-sm break-all">
                {truncateAddress(walletAddress)}
              </span>
              <button
                onClick={() => copyToClipboard(walletAddress)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 relative z-10">
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors text-sm sm:text-base"
          >
            <RefreshCw className="w-4 h-4" />
            <span>{loadingBalance ? "Refreshing..." : "Refresh"}</span>
          </button>
          <button
            onClick={() => copyToClipboard(walletAddress)}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors text-sm sm:text-base"
          >
            <Copy className="w-4 h-4" />
            <span>Copy Address</span>
          </button>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 max-w-sm w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Wallet QR Code</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="text-center space-y-4">
              <div className="bg-white p-4 rounded-2xl inline-block">
                <img
                  src={qrCodeDataURL}
                  alt="Wallet QR Code with PayzTap"
                  className="w-48 h-48"
                />
              </div>

              <p className="text-gray-400 text-xs">
                Scan this QR code to send funds to your wallet
              </p>
              <p className="text-red-400 text-xs">
                Only Fund the wallet with USDC (Base network)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Recent Transactions</h3>
          <button
            onClick={() => setShowAllTransactions(true)}
            className="text-[#61CE70] hover:text-[#50B060] transition-colors font-medium"
          >
            View All
          </button>
        </div>

        {loadingTransactions ? (
          <div className="text-center text-gray-400">
            Loading transactions...
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center text-gray-400">
            No transactions found.
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.slice(0, 5).map((tx: any) => (
              <div
                key={tx.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
              >
                {/* Left Side: Icon + Info */}
                <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                  <div
                    className={`p-3 rounded-xl shrink-0 ${
                      tx.type === "received"
                        ? "bg-[#61CE70]/20"
                        : "bg-red-500/20"
                    }`}
                  >
                    {tx.type === "received" ? (
                      <ArrowDownLeft className="w-5 h-5 text-[#61CE70]" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5 text-red-400" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium capitalize truncate">
                      {tx.type === "received" ? "Deposit" : "Transfer"}
                    </p>
                    <p className="text-gray-400 text-sm break-words">
                      {tx.type === "received" ? (
                        <span>
                          From:{" "}
                          <span className="font-bold text-white uppercase">
                            {getCounterpartyDisplay(tx)}
                          </span>
                        </span>
                      ) : (
                        <span>
                          To:{" "}
                          <span className="font-bold text-white uppercase">
                            {getCounterpartyDisplay(tx)}
                          </span>
                        </span>
                      )}
                    </p>
                    <p className="text-gray-500 text-xs">{tx.date}</p>
                  </div>
                </div>

                {/* Right Side: Amount + Actions */}
                <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                  <p
                    className={`text-base sm:text-lg font-bold ${
                      tx.type === "received" ? "text-[#61CE70]" : "text-red-400"
                    }`}
                  >
                    {tx.type === "received" ? "+" : "-"}
                    {tx.amount} USDC
                  </p>
                  <div className="flex flex-wrap sm:justify-end gap-2">
                    <button
                      onClick={() => copyToClipboard(tx.hash)}
                      className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                    >
                      <span className="hidden sm:inline">
                        {truncateAddress(tx.hash)}
                      </span>
                      <span className="sm:hidden">Hash</span>
                      <Copy className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => viewOnBlockExplorer(tx.hash)}
                      className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                    >
                      <span>Explorer</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Transactions Modal */}
      {showAllTransactions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-4 sm:p-8 w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                All Transactions
              </h3>
              <button
                onClick={() => setShowAllTransactions(false)}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Empty State */}
            {transactions.length === 0 ? (
              <div className="text-center text-gray-400 py-6">
                No transactions found.
              </div>
            ) : (
              <div className="space-y-4">
                {transactions.map((tx: any) => (
                  <div
                    key={tx.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                  >
                    {/* Left Side: Icon + Info */}
                    <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                      <div
                        className={`p-3 rounded-xl shrink-0 ${
                          tx.type === "received"
                            ? "bg-[#61CE70]/20"
                            : "bg-red-500/20"
                        }`}
                      >
                        {tx.type === "received" ? (
                          <ArrowDownLeft className="w-5 h-5 text-[#61CE70]" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-red-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium capitalize truncate">
                          {tx.type === "received" ? "Deposit" : "Transfer"}
                        </p>
                        <p className="text-gray-400 text-sm break-words">
                          {tx.type === "received"
                            ? `From: ${getCounterpartyDisplay(tx)}`
                            : `To: ${getCounterpartyDisplay(tx)}`}
                        </p>
                        <p className="text-gray-500 text-xs">{tx.date}</p>
                      </div>
                    </div>

                    {/* Right Side: Amount + Actions */}
                    <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                      <p
                        className={`text-base sm:text-lg font-bold ${
                          tx.type === "received"
                            ? "text-[#61CE70]"
                            : "text-red-400"
                        }`}
                      >
                        {tx.type === "received" ? "+" : "-"}
                        {tx.amount} USDC
                      </p>
                      <div className="flex flex-wrap sm:justify-end gap-2">
                        <button
                          onClick={() => copyToClipboard(tx.hash)}
                          className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                        >
                          <span className="hidden sm:inline">
                            {truncateAddress(tx.hash)}
                          </span>
                          <span className="sm:hidden">Hash</span>
                          <Copy className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => viewOnBlockExplorer(tx.hash)}
                          className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                        >
                          <span>Explorer</span>
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
